import React, { FC, useMemo } from 'react';
import {
  ConversationType,
  selectCurrentSelectedChat,
  selectMetadataById,
} from 'models/twilio';
import { shallowEqual, useSelector } from 'react-redux';
import styled from 'styled-components';
import ChatInterfaceTitle from './ChatInterfaceTitle';
import ChatWindowPlaceholder from './placeholders/ChatWindowPlaceholder';
import MessageDateGroup from './messages/MessageDateGroup';
import ChatInput from './ChatInput';
import { COLORS, scrollBarStyles } from '../constants';
import useTwilioChat from '../hooks/useTwilioChat';
import ChatDivider from './styled/ChatDivider';
import MessageLoadingIndicator from './styled/MessageLoadingIndicator';
import useChatSideEffect from '../hooks/useChatSideEffect';
import TwilioErrorBoundary from './error/TwilioErrorBoundary';
import ChatInterfaceFallback from './error/ChatInterfaceFallback';
import useCurrentAttendee from '../hooks/useCurrentAttendee';
import useFirebaseChatConfig from '../hooks/useFirebaseChatConfig';
import { GroupMemberList } from './GroupMemberButton';
import metadataEquality from '../utils/metadataEquality';
import ConversationImageViewer from './ConversationImageViewer';

const ChatInterfaceContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background-color: ${COLORS.GREY_M};
  position: relative;
`;

const MessageContainer = styled.div`
  padding: 8px 12px 4px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
  ${scrollBarStyles}
`;

const GroupMemberListContainer = styled(MessageContainer)`
  padding: 0;
  background-color: ${COLORS.WHITE};
`;

export interface ChatInterfaceProps {
  mobile?: boolean;
}

const ChatInterface: FC<ChatInterfaceProps> = ({ mobile = false }: ChatInterfaceProps) => {
  const { boothChatroomDisabled } = useFirebaseChatConfig();
  const currentSelectedChat = useSelector(selectCurrentSelectedChat);
  const metadata = useSelector(selectMetadataById(currentSelectedChat), metadataEquality({
    conversationType: (prev, next) => shallowEqual(prev, next),
    hasLoadedMessages: (prev, next) => shallowEqual(prev, next),
    id: (prev, next) => shallowEqual(prev, next),
  }));
  const {
    messageListRef,
    onScrollHandler,
    displayGroup,
    initialMessageFetch,
    setAllMessagesConsumed,
  } = useTwilioChat();
  const { isOrganizer } = useCurrentAttendee();

  const showGroupMembersOnly = useMemo(() => boothChatroomDisabled
    && metadata?.conversationType === ConversationType.GROUP, [metadata, boothChatroomDisabled]);

  const hideInput = useMemo(() => showGroupMembersOnly || (metadata?.conversationType === ConversationType.ANNOUNCEMENT
    && !isOrganizer), [showGroupMembersOnly, metadata, isOrganizer]);

  useChatSideEffect(messageListRef, initialMessageFetch, displayGroup, setAllMessagesConsumed);

  if (!currentSelectedChat || !metadata) return <ChatWindowPlaceholder />;
  return (
    <TwilioErrorBoundary FallbackComponent={<ChatInterfaceFallback />}>
      <ChatInterfaceContainer id="chat-interface-container">
        <ChatInterfaceTitle mobile={mobile} />
        <ChatDivider />
        {showGroupMembersOnly ? (
          <GroupMemberListContainer>
            <GroupMemberList conversationSid={currentSelectedChat} exhibitorOnly />
          </GroupMemberListContainer>
        ) : (
          <MessageContainer ref={messageListRef} onScroll={onScrollHandler}>
            {metadata.hasLoadedMessages ? displayGroup.map((dateGroupProps) => (
              <MessageDateGroup key={`MessageDateGroup-${currentSelectedChat}-${dateGroupProps.dateString}`} {...dateGroupProps} />
            )) : (
              <MessageLoadingIndicator size={30} />
            )}
          </MessageContainer>
        )}
        <ChatDivider />
        {!hideInput && <ChatInput />}
      </ChatInterfaceContainer>
      <ConversationImageViewer />
    </TwilioErrorBoundary>
  );
};

export default ChatInterface;
